import { DocumentStatusId, type DocumentVersionOption } from '@/core/models/phase4.models';
import type { ReportRow } from '@/core/models/reports.models';

// D05 (as Capium 3.0): which accounts a client may file does not depend on its size. 3.0 lists every saved
// Abridged/Filleted report for submission, whatever the company size (Accounts/Tasks/SubmitHistory.aspx.cs:110;
// Reports/Default.aspx:116). The UI no longer hides Abridged/Filleted for a medium or large company; Full stays
// submittable here because the filing flow supports it. Whether a report is eligible is the API's check at Submit.
export const REPORT_NOT_PRODUCIBLE = 'This report can no longer be produced.';

/** How a report is named on the submission filed from it; #169 matches on the "Accounts {refNo} " prefix. */
export function reportLabel(row: Pick<ReportRow, 'refNo' | 'fromDate' | 'toDate' | 'type' | 'description'>): string {
  return `${row.refNo} - ${row.fromDate} to ${row.toDate} - ${row.type}${row.description ? ' - ' + row.description : ''}`;
}

/**
 * #168/#171: whether a submission was started for this report. A submission belongs to an accounting period, not a
 * report, so the report is known only by the "Accounts {refNo} " name it was created with (the prefix #169 deletes by).
 */
export function isSubmissionFor(submission: { description?: string | null }, refNo: string): boolean {
  return (submission.description ?? '').startsWith(`Accounts ${refNo} `);
}

export function submissionDescription(
  row: Pick<ReportRow, 'refNo' | 'fromDate' | 'toDate' | 'type' | 'description'>,
): string {
  return `Accounts ${reportLabel(row)}`;
}

/** The most final accounts version that has actually been produced; newest first among equals. */
export function bestDocument(documents: DocumentVersionOption[]): DocumentVersionOption | undefined {
  const usable = [DocumentStatusId.Final, DocumentStatusId.Approved, DocumentStatusId.Generated];
  return documents
    .filter((d) => usable.includes(d.status))
    .sort((a, b) => usable.indexOf(a.status) - usable.indexOf(b.status) || b.versionNo - a.versionNo)[0];
}

/** #165: the checks AccountsReportService.ValidateAsync runs for an annual report, in plain words. */
export function validationChecksRun(row: Pick<ReportRow, 'type'>): string {
  const checks = ['company registration number format', 'director sign date recorded', 'trial balance balances'];
  if (row.type && row.type !== 'Full') {
    checks.splice(1, 0, `small-company eligibility for ${row.type} accounts`);
  }
  return checks.join(', ');
}
